import { authenticatedFetch } from '../utils/authenticatedFetch';
import { configs } from '../configs';

// User interface
export interface User {
  id: string;
  email: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  avatar?: string;
  isActive?: boolean;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

export interface UserListResponse {
  success: boolean;
  data: User[];
  total?: number;
}

export interface UserResponse {
  success: boolean;
  data: User;
  message?: string;
}

class UsersService {
  private apiRoot = configs.apiRoot;

  async getUsers(params?: {
    page?: number;
    perPage?: number;
    size?: number;
    filter?: any;
    sort?: any;
    q?: string;
  }): Promise<User[] & { __contentRange?: string; __total?: number }> {
    const queryParams = new URLSearchParams();

    const page = params?.page ?? 1;
    let size = 100;

    if (params?.size) {
      size = params.size;
    } else if (params?.perPage) {
      size = params.perPage;
    }

    queryParams.append('page', page.toString());
    queryParams.append('size', size.toString());

    if (params?.q) queryParams.append('q', params.q);
    if (params?.filter)
      queryParams.append('filter', JSON.stringify(params.filter));
    if (params?.sort) queryParams.append('sort', JSON.stringify(params.sort));

    const url = `${this.apiRoot}/users${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch users: ${response.statusText}`
      );
    }

    const result = await response.json();

    // API returns array directly (via sendFormattedResponse), not wrapped in { data: ... }
    // Also check Content-Range header for total count
    const contentRange = response.headers.get('Content-Range');
    let total = 0;

    if (contentRange) {
      // Content-Range format: "users 1-20/100"
      const match = contentRange.match(/\/(\d+)$/);
      if (match) {
        total = parseInt(match[1], 10);
      }
    }

    // Handle both array response and object with data property
    const users = Array.isArray(result) ? result : result.data || [];

    // If total not found in header, try result.total or use array length
    if (!total) {
      total = result.total || users.length;
    }

    // Attach metadata to the array
    (users as any).__total = total;
    (users as any).__contentRange = contentRange;

    return users as User[] & { __contentRange?: string; __total?: number };
  }

  async getUser(id: string): Promise<User> {
    const url = `${this.apiRoot}/users/${id}`;
    const response = await authenticatedFetch(url, { method: 'GET' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to fetch user: ${response.statusText}`
      );
    }

    const result = await response.json();
    return Array.isArray(result) ? result[0] : result.data || result;
  }

  async createUser(userData: Partial<User>): Promise<User> {
    const url = `${this.apiRoot}/users`;
    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to create user: ${response.statusText}`
      );
    }

    const result = await response.json();
    return Array.isArray(result) ? result[0] : result.data || result;
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const url = `${this.apiRoot}/users/${id}`;
    const response = await authenticatedFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to update user: ${response.statusText}`
      );
    }

    const result = await response.json();
    return Array.isArray(result) ? result[0] : result.data || result;
  }

  async deleteUser(id: string): Promise<void> {
    const url = `${this.apiRoot}/users/${id}`;
    const response = await authenticatedFetch(url, { method: 'DELETE' });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: response.statusText }));
      throw new Error(
        error.message || `Failed to delete user: ${response.statusText}`
      );
    }
  }
}

export const usersService = new UsersService();
